/**
 * MCP Configuration Page - Modern UI/UX
 * Beautiful design with smooth animations
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, TextField, Button, Stepper, Step, StepLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert,
  CircularProgress, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Card, CardContent, Grid, Divider, ToggleButtonGroup, ToggleButton,
  Checkbox, FormGroup, FormControlLabel, Stack, Switch, Fade, Collapse,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, ArrowBack as BackIcon, Storage as ServerIcon,
  CloudSync as SyncIcon, CheckCircle, CheckCircle as TestIcon, LibraryBooks as TemplateIcon,
  NavigateNext as NextIcon, NavigateBefore as PrevIcon, Refresh as RefreshIcon,
  Build as ToolIcon, Visibility as ViewIcon, Code as CodeIcon,
  Close as CloseIcon, DoneAll as DoneIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import mcpMasterService from '../services/mcpMasterService';

export default function MCPConfigurationPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'settings']);

  // State
  const [servers, setServers] = useState([]);
  const [loadingServers, setLoadingServers] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [creating, setCreating] = useState(false);

  // Tools Modal state
  const [toolsModalOpen, setToolsModalOpen] = useState(false);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [selectedServerTools, setSelectedServerTools] = useState(null);
  const [togglingServers, setTogglingServers] = useState({});

  // Wizard data
  const [wizardData, setWizardData] = useState({
    name: '',
    display_name: '',
    type: 'http',
    selectedTemplate: null,
    connectionConfig: '',
    selectedAgents: [],
  });

  const wizardSteps = [
    t('settings:mcpConfig.wizard.steps.selectType'),
    t('settings:mcpConfig.wizard.steps.enterConfig'),
    t('settings:mcpConfig.wizard.steps.testConnection'),
    t('settings:mcpConfig.wizard.steps.selectAgents')
  ];

  // Load data
  useEffect(() => {
    loadServers();
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadServers = async () => {
    setLoadingServers(true);
    try {
      console.log('Đang tải danh sách servers...');
      const response = await mcpMasterService.getGlobalServers();

      // Đảm bảo các servers có đủ thông tin
      const processedServers = (response.servers || []).map(server => {
        // Đảm bảo assigned_agents luôn là mảng
        if (!server.assigned_agents) server.assigned_agents = [];

        // Log để debug
        if (server.assigned_agents.length === 0) {
          console.log(`Server ${server.name} (${server.id}) không có agents được gán`);
        } else {
          console.log(`Server ${server.name} (${server.id}) có ${server.assigned_agents.length} agents được gán:`,
            server.assigned_agents);
        }

        return server;
      });

      console.log('Danh sách servers đã xử lý:', processedServers);
      setServers(processedServers);
    } catch (error) {
      console.error('Error loading servers:', error);
      Swal.fire({
        icon: 'error',
        title: t('settings:mcpConfig.alerts.errorLoading'),
        text: t('settings:mcpConfig.alerts.errorLoadingMessage'),
        confirmButtonText: 'OK'
      });
    } finally {
      setLoadingServers(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await mcpMasterService.getTemplates();
      const templatesData = response.templates || [];

      // Đảm bảo mỗi template có đủ thông tin
      templatesData.forEach(template => {
        // Đảm bảo có config đúng định dạng
        if (!template.config) {
          template.config = {};
        }
      });

      setTemplates(templatesData);
      console.log('Loaded templates:', templatesData);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  // Wizard controls
  const openWizard = () => {
    setWizardOpen(true);
    setActiveStep(0);
    setTestResult(null);
    setWizardData({
      name: '',
      display_name: '',
      type: 'http',
      selectedTemplate: null,
      connectionConfig: '',
      selectedAgents: [],
    });
  };

  const closeWizard = () => {
    setWizardOpen(false);
    setActiveStep(0);
    setTestResult(null);
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  // Template selection
  const handleTemplateSelect = (template) => {
    // Đảm bảo có config và chuyển đổi đúng định dạng JSON
    let configStr = '{}';
    try {
      if (template && template.config) {
        // Nếu config đã là string thì parse, nếu là object thì stringify trực tiếp
        const configObj = typeof template.config === 'string'
          ? JSON.parse(template.config)
          : template.config;
        configStr = JSON.stringify(configObj, null, 2);
      }
    } catch (err) {
      console.error('Error parsing template config:', err);
      configStr = JSON.stringify({}, null, 2);
    }

    setWizardData((prev) => ({
      ...prev,
      type: template.type,
      selectedTemplate: template,
      connectionConfig: configStr,
    }));

    // Log để debug chi tiết hơn
    console.log('Selected template:', template);
    console.log('Config JSON:', configStr);
    console.log('Config type:', typeof template.config);
    if (typeof template.config === 'object') {
      console.log('Config keys:', Object.keys(template.config));
    }
  };

  // Test connection
  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      let configObj;
      try {
        configObj = JSON.parse(wizardData.connectionConfig);
      } catch (e) {
        Swal.fire({
          icon: 'error',
          title: t('settings:mcpConfig.alerts.invalidJson'),
          text: t('settings:mcpConfig.alerts.invalidJsonMessage'),
          confirmButtonText: 'OK'
        });
        setTesting(false);
        return;
      }

      const result = await mcpMasterService.testConnectionConfig(wizardData.type, configObj);
      setTestResult(result);

      if (result.success) {
        Swal.fire({
          icon: 'success',
          title: t('settings:mcpConfig.alerts.connectionSuccess'),
          text: t('settings:mcpConfig.alerts.connectionSuccessMessage', { ms: result.latency_ms }),
          timer: 3000,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: t('settings:mcpConfig.alerts.connectionFailed'),
          text: result.message,
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      const errorMsg = error.response?.data?.detail || error.message || t('settings:mcpConfig.alerts.testError');
      Swal.fire({
        icon: 'error',
        title: t('settings:mcpConfig.alerts.testError'),
        text: errorMsg,
        confirmButtonText: 'OK'
      });
      setTestResult({ success: false, message: errorMsg });
    } finally {
      setTesting(false);
    }
  };

  // Create server
  const handleCreateServer = async () => {
    if (!wizardData.name || !wizardData.display_name) {
      Swal.fire({
        icon: 'warning',
        title: t('settings:mcpConfig.alerts.missingInfo'),
        text: t('settings:mcpConfig.alerts.missingInfoMessage'),
        confirmButtonText: 'OK'
      });
      return;
    }

    if (!testResult || !testResult.success) {
      Swal.fire({
        icon: 'warning',
        title: t('settings:mcpConfig.alerts.notTested'),
        text: t('settings:mcpConfig.alerts.notTestedMessage'),
        confirmButtonText: 'OK'
      });
      return;
    }

    setCreating(true);
    try {
      const configObj = JSON.parse(wizardData.connectionConfig);

      const serverData = {
        name: wizardData.name,
        display_name: wizardData.display_name,
        type: wizardData.type,
        connection_config: configObj,
        is_active: true,
        metadata: {
          template: wizardData.selectedTemplate?.key || 'custom',
        },
      };

      console.log("Đang tạo server mới:", serverData);
      const newServer = await mcpMasterService.createServer(serverData);
      console.log("Server đã được tạo:", newServer);

      // Gán agents
      console.log("Danh sách agents sẽ được gán:", wizardData.selectedAgents);
      const mappingResults = [];

      if (wizardData.selectedAgents.length > 0) {
        for (const agent of wizardData.selectedAgents) {
          try {
            console.log(`Đang gán agent ${agent} cho server ${newServer.id}...`);
            const mappingData = {
              is_enabled: true,
              priority: 1,
              custom_config: {}
            };

            const result = await mcpMasterService.addAgentMapping(agent, newServer.id, mappingData);
            console.log(`Kết quả gán agent ${agent}:`, result);
            mappingResults.push({ agent, success: true, result });
          } catch (error) {
            console.error(`Lỗi khi gán agent ${agent}:`, error);
            mappingResults.push({ agent, success: false, error: error.response?.data?.detail || error.message });
          }
        }

        console.log("Kết quả gán các agents:", mappingResults);
      } else {
        console.log("Không có agent nào được chọn để gán");
      }

      // Sync tools
      try {
        console.log(`Đang đồng bộ tools cho server ${newServer.id}...`);
        const syncResult = await mcpMasterService.syncTools(newServer.id);
        console.log("Kết quả đồng bộ tools:", syncResult);
      } catch (error) {
        console.error('Lỗi khi đồng bộ tools:', error);
      }

      // Kiểm tra kết quả gán agents
      const failedMappings = mappingResults.filter(m => !m.success);
      if (failedMappings.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: t('settings:mcpConfig.alerts.createSuccessWithErrors'),
          html: t('settings:mcpConfig.alerts.createSuccessMessage', { count: wizardData.selectedAgents.length }) + 
            `, nhưng có ${failedMappings.length} agent không thể gán:<br>` +
            failedMappings.map(m => `<strong>${m.agent}</strong>: ${m.error}`).join('<br>'),
          confirmButtonText: 'OK'
        });
      } else {
        Swal.fire({
          icon: 'success',
          title: t('settings:mcpConfig.alerts.createSuccess'),
          text: t('settings:mcpConfig.alerts.createSuccessMessage', { count: wizardData.selectedAgents.length }),
          timer: 3000,
          showConfirmButton: false
        });
      }

      closeWizard();
      loadServers();
    } catch (error) {
      console.error('Error creating server:', error);
      Swal.fire({
        icon: 'error',
        title: t('settings:mcpConfig.alerts.createError'),
        text: error.response?.data?.detail || t('settings:mcpConfig.alerts.createError'),
        confirmButtonText: 'OK'
      });
    } finally {
      setCreating(false);
    }
  };

  // Delete server
  const handleDeleteClick = (server) => {
    Swal.fire({
      title: t('settings:mcpConfig.alerts.deleteConfirm'),
      html: t('settings:mcpConfig.alerts.deleteConfirmMessage', { name: server.display_name }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('common:common.delete'),
      cancelButtonText: t('common:common.cancel')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await mcpMasterService.deleteServer(server.id);
          Swal.fire({
            icon: 'success',
            title: t('settings:mcpConfig.alerts.deleteSuccess'),
            text: t('settings:mcpConfig.alerts.deleteSuccessMessage'),
            timer: 2000,
            showConfirmButton: false
          });
          loadServers();
        } catch (error) {
          console.error('Error deleting server:', error);
          Swal.fire({
            icon: 'error',
            title: t('settings:mcpConfig.alerts.deleteError'),
            text: t('settings:mcpConfig.alerts.deleteErrorMessage'),
            confirmButtonText: 'OK'
          });
        }
      }
    });
  };

  // Sync tools
  const handleSyncTools = async (serverId) => {
    try {
      const result = await mcpMasterService.syncTools(serverId);
      Swal.fire({
        icon: 'success',
        title: t('settings:mcpConfig.alerts.syncSuccess'),
        text: t('settings:mcpConfig.alerts.syncSuccessMessage', { count: result.synced_count }),
        timer: 3000,
        showConfirmButton: false
      });
      loadServers();
    } catch (error) {
      console.error('Error syncing tools:', error);
      Swal.fire({
        icon: 'error',
        title: t('settings:mcpConfig.alerts.syncError'),
        text: t('settings:mcpConfig.alerts.syncErrorMessage'),
        confirmButtonText: 'OK'
      });
    }
  };

  // Toggle server on/off
  const handleToggleServer = async (server) => {
    const newStatus = !server.is_active;
    setTogglingServers(prev => ({ ...prev, [server.id]: true }));

    try {
      await mcpMasterService.toggleServer(server.id, newStatus);
      Swal.fire({
        icon: 'success',
        title: t('settings:mcpConfig.alerts.toggleSuccess', { 
          status: newStatus ? t('settings:mcpConfig.status.enabled') : t('settings:mcpConfig.status.disabled') 
        }),
        text: t('settings:mcpConfig.alerts.toggleSuccessMessage', { 
          name: server.display_name,
          action: newStatus ? t('settings:mcpConfig.status.activated') : t('settings:mcpConfig.status.deactivated')
        }),
        timer: 2000,
        showConfirmButton: false
      });
      loadServers();
    } catch (error) {
      console.error('Error toggling server:', error);
      Swal.fire({
        icon: 'error',
        title: t('settings:mcpConfig.alerts.toggleError'),
        text: t('settings:mcpConfig.alerts.toggleErrorMessage'),
        confirmButtonText: 'OK'
      });
    } finally {
      setTogglingServers(prev => ({ ...prev, [server.id]: false }));
    }
  };

  // View server tools
  const handleViewTools = async (server) => {
    setToolsLoading(true);
    setToolsModalOpen(true);
    setSelectedServerTools({ server, tools: [], loading: true });

    try {
      const result = await mcpMasterService.getServerTools(server.id);
      setSelectedServerTools({
        server,
        tools: result.tools || [],
        total: result.total || 0,
        loading: false
      });
    } catch (error) {
      console.error('Error loading tools:', error);
      setSelectedServerTools({
        server,
        tools: [],
        total: 0,
        loading: false,
        error: t('settings:mcpConfig.actions.viewTools')
      });
    } finally {
      setToolsLoading(false);
    }
  };

  const closeToolsModal = () => {
    setToolsModalOpen(false);
    setSelectedServerTools(null);
  };

  // Wizard Step 1: Chọn loại & template
  const renderStep1 = () => {
    const templatesByType = templates.reduce((acc, t) => {
      if (!acc[t.type]) acc[t.type] = [];
      acc[t.type].push(t);
      return acc;
    }, {});

    return (
      <Box>
        <Typography variant="h6" gutterBottom>{t('settings:mcpConfig.wizard.step1.title')}</Typography>
        <ToggleButtonGroup
          value={wizardData.type}
          exclusive
          onChange={(e, v) => v && setWizardData({ ...wizardData, type: v, selectedTemplate: null })}
          fullWidth
          sx={{ mb: 3 }}
        >
          <ToggleButton value="stdio">
            <Box textAlign="center">
              <Typography variant="body2" fontWeight="bold">{t('settings:mcpConfig.serverTypes.stdio')}</Typography>
              <Typography variant="caption">{t('settings:mcpConfig.serverTypes.stdioDesc')}</Typography>
            </Box>
          </ToggleButton>
          <ToggleButton value="http">
            <Box textAlign="center">
              <Typography variant="body2" fontWeight="bold">{t('settings:mcpConfig.serverTypes.http')}</Typography>
              <Typography variant="caption">{t('settings:mcpConfig.serverTypes.httpDesc')}</Typography>
            </Box>
          </ToggleButton>
          <ToggleButton value="sse">
            <Box textAlign="center">
              <Typography variant="body2" fontWeight="bold">{t('settings:mcpConfig.serverTypes.sse')}</Typography>
              <Typography variant="caption">{t('settings:mcpConfig.serverTypes.sseDesc')}</Typography>
            </Box>
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>{t('settings:mcpConfig.wizard.step1.selectTemplate')}</Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('settings:mcpConfig.wizard.step1.templateHint')}
        </Alert>
        <Grid container spacing={2}>
          {(templatesByType[wizardData.type] || []).map((template) => (
            <Grid item xs={12} sm={6} md={4} key={template.key} sx={{ display: 'flex' }}>
              <Card
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  border: wizardData.selectedTemplate?.key === template.key ? 2 : 1,
                  borderColor: wizardData.selectedTemplate?.key === template.key ? 'primary.main' : 'divider',
                  bgcolor: wizardData.selectedTemplate?.key === template.key ? 'action.selected' : 'background.paper',
                  transition: 'all 0.2s',
                  transform: wizardData.selectedTemplate?.key === template.key ? 'translateY(-2px)' : 'none',
                  boxShadow: wizardData.selectedTemplate?.key === template.key ? 2 : 0,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 1,
                    transform: 'translateY(-2px)',
                    bgcolor: wizardData.selectedTemplate?.key === template.key ? 'action.selected' : 'action.hover'
                  },
                  '&:active': {
                    boxShadow: 3,
                    transform: 'translateY(-1px)',
                    bgcolor: 'action.selected'
                  }
                }}
                onClick={() => handleTemplateSelect(template)}
              >
                <CardContent sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    {template.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {template.description}
                  </Typography>
                  {wizardData.selectedTemplate?.key === template.key && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckCircle fontSize="small" />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
            <Card
              variant="outlined"
              sx={{
                cursor: 'pointer',
                border: !wizardData.selectedTemplate ? 2 : 1,
                borderColor: !wizardData.selectedTemplate ? 'primary.main' : 'divider',
                bgcolor: !wizardData.selectedTemplate ? 'action.selected' : 'background.paper',
                transition: 'all 0.2s',
                transform: !wizardData.selectedTemplate ? 'translateY(-2px)' : 'none',
                boxShadow: !wizardData.selectedTemplate ? 2 : 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 1,
                  transform: 'translateY(-2px)',
                  bgcolor: !wizardData.selectedTemplate ? 'action.selected' : 'action.hover'
                },
                '&:active': {
                  boxShadow: 3,
                  transform: 'translateY(-1px)',
                  bgcolor: 'action.selected'
                }
              }}
              onClick={() => setWizardData({ ...wizardData, selectedTemplate: null, connectionConfig: '{}' })}
            >
              <CardContent sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  {t('settings:mcpConfig.wizard.step1.custom')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {t('settings:mcpConfig.wizard.step1.customDescription')}
                </Typography>
                {!wizardData.selectedTemplate && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CheckCircle fontSize="small" />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Wizard Step 2: Nhập JSON config
  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>{t('settings:mcpConfig.wizard.step2.title')}</Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <TextField
          label={t('settings:mcpConfig.wizard.step2.nameLabel')}
          placeholder={t('settings:mcpConfig.wizard.step2.namePlaceholder')}
          value={wizardData.name}
          onChange={(e) => setWizardData({ ...wizardData, name: e.target.value })}
          fullWidth
          helperText={t('settings:mcpConfig.wizard.step2.nameHelper')}
        />
        <TextField
          label={t('settings:mcpConfig.wizard.step2.displayNameLabel')}
          placeholder={t('settings:mcpConfig.wizard.step2.displayNamePlaceholder')}
          value={wizardData.display_name}
          onChange={(e) => setWizardData({ ...wizardData, display_name: e.target.value })}
          fullWidth
        />
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>{t('settings:mcpConfig.wizard.step2.configLabel')}</Typography>
      {wizardData.selectedTemplate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <span dangerouslySetInnerHTML={{ __html: t('settings:mcpConfig.wizard.step2.templateInfo', { name: wizardData.selectedTemplate.name }) }} />
        </Alert>
      )}
      <TextField
        multiline
        rows={15}
        value={wizardData.connectionConfig}
        onChange={(e) => setWizardData({ ...wizardData, connectionConfig: e.target.value })}
        fullWidth
        placeholder={wizardData.type === 'stdio'
          ? '{"command": "docker", "args": ["run", "-i", "--rm", "-e", "ENV_VAR", "image:tag"], "env": {"ENV_VAR": "value"}}'
          : '{"url": "https://api.example.com/mcp", "headers": {"Authorization": "Bearer token"}}'}
        sx={{
          '& textarea': {
            fontFamily: 'Monaco, Consolas, monospace',
            fontSize: '13px',
          },
        }}
      />
      {wizardData.type === 'stdio' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            {t('settings:mcpConfig.wizard.step2.stdioInfo.title')}
          </Typography>
          <Typography variant="caption" component="div">
            <span dangerouslySetInnerHTML={{ __html: t('settings:mcpConfig.wizard.step2.stdioInfo.command') }} /><br />
            <span dangerouslySetInnerHTML={{ __html: t('settings:mcpConfig.wizard.step2.stdioInfo.args') }} /><br />
            <span dangerouslySetInnerHTML={{ __html: t('settings:mcpConfig.wizard.step2.stdioInfo.env') }} />
          </Typography>
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {t('settings:mcpConfig.wizard.step2.configHint', { type: wizardData.type.toUpperCase() })}
      </Typography>
    </Box>
  );

  // Wizard Step 3: Test connection
  const renderStep3 = () => (
    <Box textAlign="center" py={4}>
      <Typography variant="h6" gutterBottom>{t('settings:mcpConfig.wizard.step3.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('settings:mcpConfig.wizard.step3.description')}
      </Typography>

      {!testResult ? (
        <Button
          variant="contained"
          size="large"
          startIcon={testing ? <CircularProgress size={20} /> : <TestIcon />}
          onClick={handleTestConnection}
          disabled={testing}
        >
          {testing ? t('settings:mcpConfig.wizard.step3.testing') : t('settings:mcpConfig.wizard.step3.testButton')}
        </Button>
      ) : (
        <Box>
          <Alert
            severity={testResult.success ? 'success' : 'error'}
            sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              {testResult.success ? t('settings:mcpConfig.wizard.step3.success') : t('settings:mcpConfig.wizard.step3.failed')}
            </Typography>
            <Typography variant="body2">{testResult.message}</Typography>
            {testResult.latency_ms && (
              <Typography variant="caption">{t('settings:mcpConfig.wizard.step3.latency', { ms: testResult.latency_ms })}</Typography>
            )}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleTestConnection}
            disabled={testing}
          >
            {t('settings:mcpConfig.wizard.step3.testAgain')}
          </Button>
        </Box>
      )}
    </Box>
  );

  // Wizard Step 4: Chọn agents
  const renderStep4 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>{t('settings:mcpConfig.wizard.step4.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('settings:mcpConfig.wizard.step4.description')}
      </Typography>

      <FormGroup>
        {['host', 'hr', 'sales'].map((agent) => (
          <FormControlLabel
            key={agent}
            control={
              <Checkbox
                checked={wizardData.selectedAgents.includes(agent)}
                onChange={(e) => {
                  const newAgents = e.target.checked
                    ? [...wizardData.selectedAgents, agent]
                    : wizardData.selectedAgents.filter((a) => a !== agent);
                  setWizardData({ ...wizardData, selectedAgents: newAgents });
                }}
              />
            }
            label={<Typography textTransform="uppercase">{agent} Agent</Typography>}
          />
        ))}
      </FormGroup>

      <Alert severity="info" sx={{ mt: 3 }}>
        {t('settings:mcpConfig.wizard.step4.guestAgentNote')}
      </Alert>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header - Modern Design */}
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            right: 100,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)'
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              mr: 2,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <BackIcon />
          </IconButton>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2
            }}
          >
            <ServerIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight="600">{t('settings:mcpConfig.pageTitle')}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {t('settings:mcpConfig.pageSubtitle')} • {t('settings:mcpConfig.serversCount', { count: servers.length })}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={loadServers}
              disabled={loadingServers}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                borderRadius: 2
              }}
            >
              {t('settings:mcpConfig.refresh')}
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={openWizard}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                fontWeight: 600,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                borderRadius: 2,
                px: 3
              }}
            >
              {t('settings:mcpConfig.addServer')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Servers Table - Modern Design */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,1) 100%)'
        }}
      >
        {loadingServers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : servers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3
              }}
            >
              <TemplateIcon sx={{ fontSize: 50, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('settings:mcpConfig.noServers')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('settings:mcpConfig.noServersHint')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openWizard}
              sx={{ borderRadius: 2, px: 3 }}
            >
              {t('settings:mcpConfig.addServerNow')}
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>{t('settings:mcpConfig.table.server')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('settings:mcpConfig.table.type')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('settings:mcpConfig.table.tools')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('settings:mcpConfig.table.agents')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('settings:mcpConfig.table.status')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>{t('settings:mcpConfig.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {servers.map((server) => (
                  <TableRow
                    key={server.id}
                    sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                      transition: 'background-color 0.2s',
                      opacity: server.is_active ? 1 : 0.6
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            background: server.is_active
                              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                              : 'linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: 14
                          }}
                        >
                          {server.display_name.charAt(0).toUpperCase()}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {server.display_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {server.name}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={server.type.toUpperCase()}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: server.type === 'http' ? 'primary.50' :
                            server.type === 'sse' ? 'secondary.50' : 'warning.50',
                          color: server.type === 'http' ? 'primary.main' :
                            server.type === 'sse' ? 'secondary.main' : 'warning.main',
                          border: '1px solid',
                          borderColor: server.type === 'http' ? 'primary.200' :
                            server.type === 'sse' ? 'secondary.200' : 'warning.200'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleViewTools(server)}
                        sx={{
                          minWidth: 'auto',
                          fontWeight: 600,
                          '&:hover': { bgcolor: 'primary.50' }
                        }}
                      >
                        <ToolIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        {server.tools_count || 0}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(server.assigned_agents || []).map((agent) => (
                          <Chip
                            key={agent}
                            label={agent.toUpperCase()}
                            size="small"
                            sx={{
                              fontSize: 10,
                              height: 22,
                              bgcolor: 'success.50',
                              color: 'success.main',
                              fontWeight: 600
                            }}
                          />
                        ))}
                        {(server.assigned_agents || []).length === 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {t('settings:mcpConfig.table.notAssigned')}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Switch
                          size="small"
                          checked={server.is_active}
                          onChange={() => handleToggleServer(server)}
                          disabled={togglingServers[server.id]}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: 'success.main',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: 'success.main',
                            },
                          }}
                        />
                        {togglingServers[server.id] && (
                          <CircularProgress size={16} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <Tooltip title={t('settings:mcpConfig.actions.viewTools')}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewTools(server)}
                            sx={{
                              bgcolor: 'primary.50',
                              '&:hover': { bgcolor: 'primary.100' }
                            }}
                          >
                            <ViewIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('settings:mcpConfig.actions.syncTools')}>
                          <IconButton
                            size="small"
                            onClick={() => handleSyncTools(server.id)}
                            sx={{
                              bgcolor: 'info.50',
                              '&:hover': { bgcolor: 'info.100' }
                            }}
                          >
                            <SyncIcon sx={{ fontSize: 18, color: 'info.main' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('settings:mcpConfig.actions.delete')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(server)}
                            sx={{
                              bgcolor: 'error.50',
                              '&:hover': { bgcolor: 'error.100' }
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 18, color: 'error.main' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create Server Wizard Dialog - Modern Design */}
      <Dialog
        open={wizardOpen}
        onClose={closeWizard}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 2.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 45,
                height: 45,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ServerIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="600">{t('settings:mcpConfig.wizard.title')}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {t('settings:mcpConfig.wizard.step', { 
                  current: activeStep + 1, 
                  total: wizardSteps.length, 
                  stepName: wizardSteps[activeStep] 
                })}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={closeWizard}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stepper
            activeStep={activeStep}
            sx={{
              mb: 4,
              mt: 2,
              '& .MuiStepLabel-root .Mui-completed': {
                color: 'success.main',
              },
              '& .MuiStepLabel-root .Mui-active': {
                color: 'primary.main',
              },
            }}
          >
            {wizardSteps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      '&.Mui-completed': { color: 'success.main' },
                      '&.Mui-active': { color: 'primary.main' }
                    }
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={activeStep === index ? 600 : 400}
                    color={activeStep === index ? 'primary.main' : 'text.secondary'}
                  >
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Fade in={true} timeout={300}>
            <Box>
              {activeStep === 0 && renderStep1()}
              {activeStep === 1 && renderStep2()}
              {activeStep === 2 && renderStep3()}
              {activeStep === 3 && renderStep4()}
            </Box>
          </Fade>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          <Button
            onClick={closeWizard}
            sx={{ borderRadius: 2 }}
          >
            {t('settings:mcpConfig.wizard.buttons.cancel')}
          </Button>
          <Box sx={{ flex: 1 }} />
          {activeStep > 0 && (
            <Button
              startIcon={<PrevIcon />}
              onClick={handleBack}
              sx={{ borderRadius: 2, mr: 1 }}
            >
              {t('settings:mcpConfig.wizard.buttons.back')}
            </Button>
          )}
          {activeStep < wizardSteps.length - 1 && (
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={handleNext}
              disabled={activeStep === 2 && (!testResult || !testResult.success)}
              sx={{
                borderRadius: 2,
                px: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                }
              }}
            >
              {t('settings:mcpConfig.wizard.buttons.next')}
            </Button>
          )}
          {activeStep === wizardSteps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleCreateServer}
              disabled={creating || !testResult || !testResult.success}
              startIcon={creating ? <CircularProgress size={20} color="inherit" /> : <DoneIcon />}
              sx={{
                borderRadius: 2,
                px: 3,
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0e8a7d 0%, #2ed66c 100%)',
                }
              }}
            >
              {creating ? t('settings:mcpConfig.wizard.buttons.creating') : t('settings:mcpConfig.wizard.buttons.create')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Tools Modal - View Server Tools */}
      <Dialog
        open={toolsModalOpen}
        onClose={closeToolsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: 'white',
            py: 2.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 45,
                height: 45,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ToolIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="600">
                {selectedServerTools?.server?.display_name || 'Server Tools'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {t('settings:mcpConfig.actions.toolsAvailable', { count: selectedServerTools?.total || 0 })}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={closeToolsModal}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {toolsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : selectedServerTools?.error ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Alert severity="error" sx={{ mx: 3 }}>
                {selectedServerTools.error}
              </Alert>
            </Box>
          ) : selectedServerTools?.tools?.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <ToolIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('settings:mcpConfig.actions.noTools')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('settings:mcpConfig.actions.noToolsHint')}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={() => {
                  closeToolsModal();
                  handleSyncTools(selectedServerTools?.server?.id);
                }}
              >
                {t('settings:mcpConfig.actions.syncNow')}
              </Button>
            </Box>
          ) : (
            <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              {selectedServerTools?.tools?.map((tool, index) => (
                <Box
                  key={tool.tool_name || index}
                  sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'grey.50' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <CodeIcon sx={{ fontSize: 20, color: 'white' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        {tool.tool_name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {tool.description || t('settings:mcpConfig.actions.noDescription')}
                      </Typography>
                      {tool.parameters_schema && Object.keys(tool.parameters_schema).length > 0 && (
                        <Collapse in={true}>
                          <Box
                            sx={{
                              bgcolor: 'grey.100',
                              borderRadius: 1.5,
                              p: 1.5,
                              fontFamily: 'monospace',
                              fontSize: 11,
                              overflow: 'auto',
                              maxHeight: 150
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" fontWeight="600" gutterBottom display="block">
                              {t('settings:mcpConfig.actions.parametersSchema')}
                            </Typography>
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {JSON.stringify(tool.parameters_schema, null, 2)}
                            </pre>
                          </Box>
                        </Collapse>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          <Button onClick={closeToolsModal} sx={{ borderRadius: 2 }}>
            {t('settings:mcpConfig.actions.close')}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={() => {
              closeToolsModal();
              handleSyncTools(selectedServerTools?.server?.id);
            }}
            sx={{ borderRadius: 2 }}
          >
            {t('settings:mcpConfig.actions.syncAgain')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
